import { defineTable } from "convex/server";
import { v } from "convex/values";

export const itemTypes = v.union(
  v.literal("product"),
  v.literal("service"),
  v.literal("custom"),
);

export const validatorQuotationItem = v.object({
  quotationId: v.id("quotations"), // References quotations table
  // `null` es la señal explícita de "desvincular el ítem del producto/servicio"
  // desde el form (acción "Convertir a custom"). El patch en el server la
  // propaga para borrar el campo del documento.
  productId: v.optional(v.union(v.id("products"), v.null())),
  name: v.string(), // Item name
  description: v.optional(v.string()), // Item description
  quantity: v.number(), // Item quantity
  unitPrice: v.number(), // Unit price
  unit: v.string(), // Unit of measurement (e.g., "unidad", "kg", "m")
  taxRate: v.number(), // Tax rate percentage

  isExempt: v.optional(v.boolean()), // f) Indicar si el ítem está exento
  exemptRate: v.optional(v.number()), // f) Tasa de exención del ítem
  exemptAmount: v.optional(v.number()), // f) Monto exento del ítem
  discountRate: v.optional(v.number()), // g) Porcentaje de descuento
  discountAmount: v.optional(v.number()), // g) Monto de descuento aplicado

  subtotal: v.number(), // Calculated subtotal
  taxAmount: v.number(), // Calculated tax amount
  totalAmount: v.number(), // Calculated total amount
  sortOrder: v.optional(v.number()), // Order for sorting items
  productCode: v.optional(v.string()), // Product code reference
  purchasePrice: v.optional(v.number()), // Purchase price for margin calculation
  type: v.optional(itemTypes), // Item type (product/service/custom)
  notes: v.optional(v.string()), // Additional notes
  createdAt: v.optional(v.number()), // Timestamp
  updatedAt: v.optional(v.number()), // Timestamp
});

/**
 * Validador de un ítem nuevo tal como llega del cliente en un borrador CRDT.
 * Es el validador de `itemsToCreate` reusado por:
 *   - `syncQuotationItems` / `syncQuotationItemsInternal` (mutations.ts).
 *   - `commitQuotationDraft` (crdt/commit.ts) — para validar el payload del
 *     doc Automerge antes de aplicar.
 */
export const validatorQuotationItemCreatePayload = v.object({
  name: v.string(),
  description: v.optional(v.string()),
  quantity: v.number(),
  unitPrice: v.number(),
  unit: v.optional(v.string()),
  taxRate: v.optional(v.number()),
  discountRate: v.optional(v.number()),
  exemptRate: v.optional(v.number()),
  sortOrder: v.optional(v.number()),
  productCode: v.optional(v.string()),
  purchasePrice: v.optional(v.number()),
  type: v.optional(itemTypes),
  notes: v.optional(v.string()),
  productId: v.optional(v.union(v.id("products"), v.null())),
});

/**
 * Validador de un ítem existente que se actualiza tal como llega del cliente.
 * Mismas reglas que `validatorQuotationItemCreatePayload` pero el `itemId` es
 * OBLIGATORIO (es lo que identifica la fila a patchear). Reusado por
 * `syncQuotationItems` y por el commit action.
 */
export const validatorQuotationItemUpdatePayload = v.object({
  itemId: v.id("quotations_items"),
  name: v.optional(v.string()),
  description: v.optional(v.string()),
  quantity: v.number(),
  unitPrice: v.number(),
  unit: v.optional(v.string()),
  taxRate: v.optional(v.number()),
  discountRate: v.optional(v.number()),
  exemptRate: v.optional(v.number()),
  sortOrder: v.optional(v.number()),
  notes: v.optional(v.string()),
  // Antes eran inmutables tras crear la fila (la design doc del form asumía
  // producto fijo por ítem). El combobox inline de la celda Nombre ahora
  // permite re-vincular la fila a otro producto/servicio, así que necesitamos
  // poder refrescar estos campos también. Sin esto, al refrescar la página
  // los ítems quedaban con el `customCode` de `addBlankItem` en lugar del
  // código real del producto seleccionado.
  productCode: v.optional(v.string()),
  // Igual que en el validador de tabla: `null` desvincula el ítem.
  productId: v.optional(v.union(v.id("products"), v.null())),
  purchasePrice: v.optional(v.number()),
  type: v.optional(itemTypes),
});

export const quotations_items = defineTable(validatorQuotationItem)
  .index("by_quotation_id", ["quotationId"])
  .index("by_product_code", ["productCode"])
  .index("by_product_id", ["productId"])
  .index("by_type", ["type"])
  .index("by_sort_order", ["quotationId", "sortOrder"]);
